import { isAxiosError } from 'axios';

import { buildPdfFilename } from 'commonUtils/buildPdfFilename';
import { BFF_PROXY_API } from 'constants/apiRoutes';

import { axiosWithoutRetries } from 'shared/api/requestInstance';
import { sendSentryClientErrorOnce } from 'shared/lib/sendSentryClientError';

type DownloadDocumentMode = 'download' | 'open';

type DownloadDocumentParams = {
  path: string;
  filename?: string;
  contentType?: string;
  mode?: DownloadDocumentMode;
};

const POPUP_BLOCKED_ERROR = 'Popup blocked';

const triggerBrowserDownload = (blob: Blob, filename: string) => {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = objectUrl;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
};

const openPreviewTab = (): Window => {
  const tab = window.open('about:blank', '_blank');

  if (!tab) {
    throw new Error(POPUP_BLOCKED_ERROR);
  }

  tab.opener = null;

  return tab;
};

const setBlobToPreviewTab = (tab: Window, blob: Blob) => {
  const objectUrl = URL.createObjectURL(blob);

  tab.location.href = objectUrl;
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
};

export const downloadDocument = async ({
  path,
  filename,
  contentType,
  mode = 'open',
}: DownloadDocumentParams): Promise<void> => {
  const previewTab = mode === 'open' ? openPreviewTab() : null;

  try {
    const { data } = await axiosWithoutRetries.post<Blob>(
      BFF_PROXY_API.downloadDocument,
      {
        path,
        ...(filename ? { filename } : {}),
        ...(contentType ? { contentType } : {}),
      },
      {
        responseType: 'blob',
      },
    );

    if (mode === 'download') {
      triggerBrowserDownload(data, buildPdfFilename(filename));
      return;
    }

    setBlobToPreviewTab(previewTab!, data);
  } catch (error) {
    previewTab?.close();

    sendSentryClientErrorOnce('downloadDocument', error as Error, {
      filename,
      path,
      status: isAxiosError(error) ? error.response?.status : undefined,
    });

    throw error;
  }
};
